import styled, { css } from 'styled-components';
import { forwardRef } from 'react';
import getConfig from 'next/config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import * as Layout from '_components/atomic/layouts/form';
import Icon from '_components/atomic/Icon';
import BaseBannerSection from '_components/composed/sections/BaseBannerSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import Button from '_components/atomic/Button';
import { useRouter } from 'next/router';
import { ROUTES } from '_constants/routes';
import PriceSection from '_components/composed/sections/PriceSection';
import { useSelector } from 'react-redux';
import { selectPriceMapByProcessingCode } from '_redux/variants/selector';
import { PROCESSING_OPTIONS_KEYS } from '_constants/processing';
import { PRODUCT_CODES } from '_constants/products';

const { publicRuntimeConfig } = getConfig();
const { colors, devices, spaces } = theme;

const StyledRequestForExtensionPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${devices.tablet} {
    display: block;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const RequestForExtensionPage = () => {
  const router = useRouter();

  const priceMapByStandardProcessing = useSelector(
    selectPriceMapByProcessingCode(PROCESSING_OPTIONS_KEYS.standard)
  );

  const productPrice = priceMapByStandardProcessing[PRODUCT_CODES.tmExtension];
  return (
    <StyledRequestForExtensionPage>
      <BaseBannerSection
        hideImageMobile
        imgSrc={`${publicRuntimeConfig.basePath}/images/request-for-extension.svg`}
        title="File Your Statement of Use Extension Online"
        text={
          <>
            Need more time to file your Trademark Statement of Use Extension
            with the USPTO?
            <br />
            Let us help you extend your deadline for another six months.
          </>
        }
        buttons={[
          {
            text: 'Start my extension filing',
            color: theme.colors.blue[500],
            onClick: () => router.push(ROUTES.requestForExtensionFiling),
            suffix: (
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.white} />
            ),
          },
        ]}
      />
      <Layout.Divider desktop size={spaces.large24X} />
      <PriceSection
        title="Request for Extension"
        subtitle="NEED MORE TIME TO FILE YOUR STATEMENT OF USE? WE CAN HELP EXTEND THE DEADLINE FOR ANOTHER 6 MONTHS."
        price={productPrice}
        priceText="USPTO gov fees"
        bullets={[
          'Expert handling with strict adherence to USPTO guidelines',
          'Consistent updates and guidance',
        ]}
        buttons={[
          {
            text: 'Start my extension filing',
            color: colors.white,
            textColor: colors.blue[500],
            suffix: (
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.blue[500]} />
            ),
            onClick: () => router.push(ROUTES.requestForExtensionFiling),
          },
        ]}
      />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={theme.colors.blue[500]}
              />
            }
            color={theme.colors.white}
            onClick={() => router.push(ROUTES.requestForExtensionFiling)}
            textColor={theme.colors.blue[500]}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.blue[500]}
          >
            Start my extension filing
          </Button>
        }
        imgSrc={`${publicRuntimeConfig.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle
        size={686}
        rotate={360}
        left={-274}
        top={'calc(50% + 20px)'}
      />
      <StyledImageContainer left={65} top={'calc(69% + 14px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledRequestForExtensionPage>
  );
};

export default RequestForExtensionPage;
